import { useState, useEffect } from 'react'
import { afterSocialConnect } from '@/lib/setupSteps'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { handleTikTokCallback, confirmTikTokInfo } from '@/lib/api'
import { SimpleIcon } from '@adthlete/common'
import { Check, X, Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

const STEP_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  SUCCESS: 'success',
  ERROR: 'error',
}

function TikTokAuthSteps() {
  const location = useLocation()
  const navigate = useNavigate()
  const { refreshUser, athlete } = useAuth()

  const [steps, setSteps] = useState([
    {
      id: 1,
      title: 'Visit TikTok and provide authorization',
      description: 'Redirecting you to TikTok to grant permissions',
      status: STEP_STATUS.PENDING,
      error: null,
    },
    {
      id: 2,
      title: 'Verifying your TikTok account using the authorization code',
      description: 'Processing the authorization code from TikTok',
      status: STEP_STATUS.PENDING,
      error: null,
    },
    {
      id: 3,
      title: 'Confirming your TikTok account',
      description: 'Fetching your TikTok profile and linking it to your account',
      status: STEP_STATUS.PENDING,
      error: null,
    },
  ])

  const [currentStep, setCurrentStep] = useState(0)
  const [connectionInfo, setConnectionInfo] = useState<any>(null)
  const [isComplete, setIsComplete] = useState(false)
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [pendingSocialId, setPendingSocialId] = useState<string | undefined>(undefined)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const code = queryParams.get('code')
    const scopes = queryParams.get('scopes')
    const state = queryParams.get('state')
    const resume = queryParams.get('resume') === '1'
    const errorParam = queryParams.get('error')
    const errorDescription = queryParams.get('error_description')

    if (code) {
      // We're coming back from TikTok with an authorization code
      handleTikTokCallbackData(code, scopes, state)
    } else if (errorParam) {
      // There was an error during TikTok authorization
      updateStepStatus(1, STEP_STATUS.ERROR, errorDescription || 'TikTok authorization failed')
    } else if (resume) {
      // We already have a pending token; continue in-app confirmation without reauth
      resumePendingConfirmation()
    } else {
      // No code means we need to start the TikTok authorization process
      startTikTokAuth()
    }
  }, [location.search])

  const updateStepStatus = (stepId: number, status: string, error: string | null = null) => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => (step.id === stepId ? { ...step, status, error } : step)),
    )
  }

  const startTikTokAuth = () => {
    // Step 1: Redirect to TikTok (this should already be complete if we're here)
    updateStepStatus(1, STEP_STATUS.SUCCESS)
    setCurrentStep(1)
  }

  const loadTikTokPreview = async (socialId?: string) => {
    updateStepStatus(3, STEP_STATUS.IN_PROGRESS)
    setCurrentStep(3)

    const previewResponse = await confirmTikTokInfo({
      social_account_id: socialId,
      confirm: false,
    })

    if (previewResponse.error || !previewResponse.success) {
      updateStepStatus(
        3,
        STEP_STATUS.ERROR,
        previewResponse.error || 'Failed to load account info',
      )
      return false
    }

    setConnectionInfo(previewResponse.user_info || { username: 'TikTok User' })
    setAwaitingConfirmation(true)
    return true
  }

  const resumePendingConfirmation = async () => {
    try {
      updateStepStatus(1, STEP_STATUS.SUCCESS)
      updateStepStatus(2, STEP_STATUS.SUCCESS)

      const pending_social_id = localStorage.getItem('pending_social_id') || undefined
      setPendingSocialId(pending_social_id)

      await loadTikTokPreview(pending_social_id)
    } catch (error: any) {
      updateStepStatus(3, STEP_STATUS.ERROR, error.message || 'Failed to load pending account')
    }
  }

  const finalizeTikTokConfirmation = async () => {
    try {
      setIsFinalizing(true)
      updateStepStatus(3, STEP_STATUS.IN_PROGRESS)
      setCurrentStep(3)

      const confirmResponse = await confirmTikTokInfo({
        social_account_id: pendingSocialId,
        confirm: true,
      })

      if (confirmResponse.error || !confirmResponse.success) {
        updateStepStatus(
          3,
          STEP_STATUS.ERROR,
          confirmResponse.error || 'Failed to confirm account',
        )
        return
      }

      updateStepStatus(3, STEP_STATUS.SUCCESS)
      setConnectionInfo(confirmResponse.user_info || connectionInfo || { username: 'TikTok User' })

      localStorage.removeItem('pending_social_id')

      await refreshUser()

      navigate(afterSocialConnect(athlete), { replace: true })
    } catch (error: any) {
      updateStepStatus(3, STEP_STATUS.ERROR, error.message || 'Failed to confirm account')
    } finally {
      setIsFinalizing(false)
    }
  }

  const handleTikTokCallbackData = async (
    code: string,
    scopes: string | null,
    state: string | null,
  ) => {
    try {
      // Step 1 is complete (we got the code)
      updateStepStatus(1, STEP_STATUS.SUCCESS)

      // Step 2: Process the authorization code and verify token
      updateStepStatus(2, STEP_STATUS.IN_PROGRESS)
      setCurrentStep(2)

      const pending_social_id = localStorage.getItem('pending_social_id')
      setPendingSocialId(pending_social_id || undefined)

      const response = await handleTikTokCallback({
        code: code,
        scopes: scopes || undefined,
        state: state || undefined,
        social_account_id: pending_social_id || undefined,
      })

      if (response.error) {
        updateStepStatus(2, STEP_STATUS.ERROR, response.error)
        return
      }

      updateStepStatus(2, STEP_STATUS.SUCCESS)

      await loadTikTokPreview(pending_social_id || undefined)
    } catch (error: any) {
      updateStepStatus(
        2,
        STEP_STATUS.ERROR,
        error.message || 'Failed to process authorization code',
      )
    }
  }

  const getStepIcon = (step: any) => {
    switch (step.status) {
      case STEP_STATUS.SUCCESS:
        return <Check className="text-green-500 h-6 w-6" />
      case STEP_STATUS.ERROR:
        return <X className="text-red-500 h-6 w-6" />
      case STEP_STATUS.IN_PROGRESS:
        return <Loader2 className="text-blue-500 h-6 w-6 animate-spin" />
      default:
        return (
          <div className="w-6 h-6 rounded-full bg-gray-500 flex items-center justify-center text-foreground text-sm font-bold">
            {step.id}
          </div>
        )
    }
  }

  const getStepClass = (step: any) => {
    switch (step.status) {
      case STEP_STATUS.SUCCESS:
        return 'border-green-500 bg-green-500/10'
      case STEP_STATUS.ERROR:
        return 'border-red-500 bg-red-500/10'
      case STEP_STATUS.IN_PROGRESS:
        return 'border-blue-500 bg-blue-500/10'
      default:
        return 'border-foreground/20 bg-card'
    }
  }

  return (
    <div className="w-full max-w-4xl mx-auto pb-4">
      <div className="mb-4 md:mb-6 text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-black p-4 rounded-full">
            <SimpleIcon slug="tiktok" color="white" height={32} width={32} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-foreground">TikTok Authorization Process</h1>
        <p className="text-gray-600">Connecting your TikTok account...</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-6">
        <div className="space-y-4">
          {steps.map((step) => (
            <div key={step.id} className={`border rounded-lg p-4 ${getStepClass(step)}`}>
              <div className="flex items-center gap-4">
                <div className="shrink-0">{getStepIcon(step)}</div>
                <div className="grow">
                  <h3 className="text-foreground font-medium">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                  {step.error && <div className="text-red-600 mt-2 text-sm">{step.error}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Success State */}
      {isComplete && connectionInfo && (
        <div className="text-center">
          <div className="bg-green-500/10 border border-green-500 rounded-lg p-6">
            <div className="flex justify-center mb-4">
              <Check className="text-green-500 h-12 w-12" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Successfully Connected!</h2>
            <div className="mb-4">
              {connectionInfo.avatar_url && (
                <img
                  src={connectionInfo.avatar_url}
                  alt="TikTok Profile"
                  className="rounded-full mb-2 mx-auto"
                  style={{ width: '80px', height: '80px' }}
                />
              )}
              <p className="text-foreground font-medium">@{connectionInfo.username}</p>
              {connectionInfo.display_name && (
                <p className="text-gray-600">{connectionInfo.display_name}</p>
              )}
              {connectionInfo.follower_count && (
                <p className="text-gray-600">
                  Followers: {connectionInfo.follower_count.toLocaleString()}
                </p>
              )}
            </div>
            <p className="text-gray-700 mb-4">Redirecting you back to settings...</p>
            <Link
              to="/settings"
              className="inline-block px-6 py-2 bg-[#C0FD72] text-black rounded-lg font-medium hover:bg-[#d2ff9c] transition-colors"
            >
              Continue to Settings
            </Link>
          </div>
        </div>
      )}

      {/* Username Confirmation State */}
      {!isComplete && awaitingConfirmation && connectionInfo && (
        <div className="text-center mb-6">
          <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-2">Confirm Your TikTok Account</h2>
            <p className="text-gray-700 mb-4">Is this the TikTok account you want to connect?</p>

            <div className="mb-6">
              {connectionInfo.avatar_url && (
                <img
                  src={connectionInfo.avatar_url}
                  alt="TikTok Profile"
                  className="rounded-full mb-2 mx-auto"
                  style={{ width: '80px', height: '80px' }}
                />
              )}
              <p className="text-foreground font-medium text-lg">@{connectionInfo.username}</p>
              {connectionInfo.display_name && (
                <p className="text-gray-600">{connectionInfo.display_name}</p>
              )}
              {connectionInfo.follower_count && (
                <p className="text-gray-600">
                  Followers: {connectionInfo.follower_count.toLocaleString()}
                </p>
              )}
            </div>

            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={finalizeTikTokConfirmation}
                disabled={isFinalizing}
                className="px-5 py-2.5 bg-[#C0FD72] text-black rounded-lg font-medium hover:bg-[#d2ff9c] transition-colors disabled:opacity-60"
              >
                {isFinalizing ? 'Confirming account…' : 'Yes, this is my account'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/connect/tiktok')}
                disabled={isFinalizing}
                className="px-5 py-2.5 bg-muted text-foreground rounded-lg font-medium hover:bg-foreground/20 transition-colors"
              >
                No, try again
              </button>
            </div>

            {isFinalizing && (
              <div className="mt-4 text-sm text-blue-800 flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Finalizing connection… please wait
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Actions */}
      {steps.some((step) => step.status === STEP_STATUS.ERROR) && !isComplete && (
        <div className="text-center">
          <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-6">
            <h3 className="text-foreground font-medium mb-2">Need Help?</h3>
            <p className="text-gray-600 mb-4">If you're experiencing issues, please check that:</p>
            <ul className="text-left text-gray-600 text-sm mb-4 space-y-1">
              <li>• Your TikTok account is accessible and not restricted</li>
              <li>• You have granted the necessary permissions</li>
              <li>• You're signing in to the correct TikTok account</li>
            </ul>
            <div className="space-x-4">
              <Link
                to="/connect/tiktok"
                className="inline-block px-4 py-2 bg-[#C0FD72] text-black rounded-lg font-medium hover:bg-[#d2ff9c] transition-colors"
              >
                Try Again
              </Link>
              <a
                href="mailto:support@athios.ai"
                className="inline-block px-4 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-foreground/20 transition-colors"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TikTokAuthSteps
